/**
 * An organization's rules on targets — `policy.targets` in its settings
 * (2026-10-26, Claude Design « Proposition 26 octobre - Profils
 * d'environnement », frames under Acme): what a member's machine may not do
 * with a target, whatever its workspaces say. The targets themselves are each
 * workspace's (`settings/targets.ts`), shared with the workspace's document.
 *
 *   noProduction  no session on a target marked Production, on any machine
 *                 of the organization's members
 *   forceHost     every target is reached through the host: Direct access is
 *                 off, so no target's secret is ever substituted in a machine
 *   noAlwaysRun   every read through the host is asked for — Always run is off
 *   forceReview   every target's results are reviewed before the agent gets
 *                 them, whatever its person ticked (owner's decision Q7 of the
 *                 same proposition: « une organisation peut imposer Review
 *                 results before sending, comme elle impose le mode »)
 *
 * Without a policy, nothing is imposed: each target has the mode its person
 * chose, production included (the owner's call, 2026-10-26).
 */
import { isPlainObject } from '../workspace/shape';

export interface OrgTargets {
  noProduction: boolean;
  forceHost: boolean;
  noAlwaysRun: boolean;
  forceReview: boolean;
}

export const EMPTY_ORG_TARGETS: OrgTargets = { noProduction: false, forceHost: false, noAlwaysRun: false, forceReview: false };
const KEYS = ['noProduction', 'forceHost', 'noAlwaysRun', 'forceReview'] as const;

/** As a stored document carries it: a section that does not read is no rule rather than a failure. */
export function orgTargetsOf(raw: unknown): OrgTargets {
  if (!isPlainObject(raw)) return EMPTY_ORG_TARGETS;
  return {
    noProduction: raw.noProduction === true,
    forceHost: raw.forceHost === true,
    noAlwaysRun: raw.noAlwaysRun === true,
    forceReview: raw.forceReview === true
  };
}

/** As an administrator wrote it — refused with a sentence per fault, never filtered. */
export function parseOrgTargets(input: unknown, problems: string[]): OrgTargets {
  if (input === undefined || input === null) return EMPTY_ORG_TARGETS;
  const at = 'policy.targets';
  if (!isPlainObject(input)) {
    problems.push(`${at}: Expected an object.`);
    return EMPTY_ORG_TARGETS;
  }
  for (const key of Object.keys(input)) {
    if (!(KEYS as readonly string[]).includes(key)) problems.push(`${at}.${key}: Not a field this version of Airlock knows.`);
  }
  for (const key of KEYS) {
    if (input[key] !== undefined && typeof input[key] !== 'boolean') problems.push(`${at}.${key}: Expected true or false.`);
  }
  return orgTargetsOf(input);
}

/** What a member's machine is held to, each rule with the first organization that sets it — null where none does. */
export interface MemberTargets {
  noProduction: string | null;
  forceHost: string | null;
  noAlwaysRun: string | null;
  forceReview: string | null;
}

export function memberTargets(orgs: readonly { name: string; targets: OrgTargets }[]): MemberTargets {
  const first = (key: keyof OrgTargets): string | null => orgs.find((one) => one.targets[key])?.name ?? null;
  return {
    noProduction: first('noProduction'),
    forceHost: first('forceHost'),
    noAlwaysRun: first('noAlwaysRun'),
    forceReview: first('forceReview')
  };
}

export const NO_MEMBER_TARGETS: MemberTargets = { noProduction: null, forceHost: null, noAlwaysRun: null, forceReview: null };

/** Whether any rule holds — an organization that decides nothing of targets gives no layer for them. */
export function targetsDecide(org: MemberTargets): boolean {
  return !!(org.noProduction || org.forceHost || org.noAlwaysRun || org.forceReview);
}

/** The sentences a refusal says, Claude Design's. */
export const targetRuleWords = {
  noProduction: (org: string): string => `${org} allows no production target on this computer — ask an admin of ${org}.`,
  forceHost: (org: string): string => `${org} reaches every target through the host — Direct access is off on this computer.`,
  noAlwaysRun: (org: string): string => `${org} asks a person for each read — Always run is off.`,
  forceReview: (org: string): string => `${org} has every result reviewed before the agent gets it.`,
  /** A target an organization shares, inert until its secret has a value here (Q10). */
  sharedSecret: (target: string, secret: string): string =>
    `Not on this computer yet — set ${secret} in Settings › Targets to use ${target}.`
};
